import { Injectable } from '@angular/core';
import type { Cell, Row, Worksheet } from 'exceljs';
import type { ExpenseLedgerItem } from './expense-ledger.service';

export interface ExpenseLedgerExportFilters {
  dateFrom: string;
  dateTo: string;
  category: string;
  status: string;
  searchTerm: string;
}

@Injectable({ providedIn: 'root' })
export class ExpenseLedgerExportService {
  async export(items: ExpenseLedgerItem[], filters: ExpenseLedgerExportFilters): Promise<void> {
    const excelJsModule = (await import('exceljs')) as unknown as {
      default: typeof import('exceljs');
    };
    const workbook = new excelJsModule.default.Workbook();
    workbook.creator = 'All To Trips';
    workbook.created = new Date();
    workbook.modified = new Date();

    const worksheet = workbook.addWorksheet('รายการรายจ่าย', {
      properties: { defaultRowHeight: 20 },
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    });

    this.configureColumns(worksheet);
    this.addReportHeader(worksheet, filters, items.length);
    this.addTable(worksheet, items);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `รายการรายจ่าย-${filters.dateFrom || 'all'}-${filters.dateTo || 'all'}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  private configureColumns(worksheet: Worksheet): void {
    const widths = [
      8, 14, 24, 20, 22, 30, 20, 38, 18, 18, 38, 26, 20, 20, 26, 16, 14, 38,
    ];
    widths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });
  }

  private addReportHeader(
    worksheet: Worksheet,
    filters: ExpenseLedgerExportFilters,
    itemCount: number,
  ): void {
    worksheet.mergeCells('A1:R1');
    const title = worksheet.getCell('A1');
    title.value = `รายงานรายการรายจ่าย${filters.dateFrom && filters.dateTo ? ` (${filters.dateFrom} ถึง ${filters.dateTo})` : ''}`;
    title.font = { bold: true, size: 16, color: { argb: 'FF0F172A' } };
    title.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 28;

    const queryRows: Array<[string, string | number]> = [
      ['วันที่เริ่มต้น', filters.dateFrom || 'ทั้งหมด'],
      ['วันที่สิ้นสุด', filters.dateTo || 'ทั้งหมด'],
      ['ประเภทรายจ่าย', filters.category],
      ['สถานะ', filters.status],
      ['คำค้นหา', filters.searchTerm || 'ไม่ได้ระบุ'],
      ['จำนวนรายการ', itemCount],
    ];

    for (let index = 0; index < queryRows.length; index += 2) {
      const leftQuery = queryRows[index];
      const rightQuery = queryRows[index + 1];
      const row = worksheet.addRow([]);

      row.getCell(6).value = leftQuery[0];
      row.getCell(7).value = leftQuery[1];
      if (rightQuery) {
        row.getCell(10).value = rightQuery[0];
        row.getCell(11).value = rightQuery[1];
      }

      [6, 10].forEach((column) => {
        const cell = row.getCell(column);
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle' };
        cell.border = this.thinBorder();
      });
      [7, 11].forEach((column) => {
        const cell = row.getCell(column);
        cell.alignment = { vertical: 'middle' };
        cell.border = this.thinBorder();
      });
    }
    worksheet.addRow([]);
  }

  private addTable(worksheet: Worksheet, items: ExpenseLedgerItem[]): Row {
    const headerRow = worksheet.addRow([
      'ลำดับ',
      'วันที่จ่าย',
      'เลขรายการจ่าย',
      'วันเวลาโอน',
      'เลข Booking',
      'ชื่อลูกค้า',
      'รหัสทัวร์',
      'ชื่อทัวร์',
      'ประเทศ',
      'ประเภทรายจ่าย',
      'รายละเอียด',
      'ผู้เบิก',
      'ช่องทางชำระ',
      'สถานะ',
      'ยอดจ่าย',
      'ผู้อนุมัติ',
      'วันที่อนุมัติ',
      'เวลาอนุมัติ',
    ]);
    this.styleHeaderRow(headerRow, 18);

    items.forEach((item, index) => {
      const approvedDate = item.approvedAt ? this.formatThaiDate(item.approvedAt) : '';
      const approvedTime = item.approvedAt ? this.formatThaiTime(item.approvedAt) : '';
      const row = worksheet.addRow([
        index + 1,
        this.toExcelCalendarDate(item.paymentDate),
        item.transactionNo,
        item.transferredAt ? new Date(item.transferredAt) : '',
        item.bookingNo,
        item.customerName,
        item.tourCode,
        item.tourName,
        item.country,
        this.categoryLabel(item.category),
        item.description,
        item.requestedByName,
        this.paymentMethodLabel(item),
        this.statusLabel(item.status),
        item.amount,
        item.approvedByName,
        approvedDate,
        approvedTime,
      ]);
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = this.thinBorder();
        cell.alignment = { vertical: 'top', wrapText: true };
      });
      row.getCell(2).numFmt = 'dd/mm/yyyy';
      row.getCell(4).numFmt = 'dd/mm/yyyy hh:mm';
      row.getCell(15).numFmt = '#,##0.00';
    });

    const expenseTotal = items.reduce(
      (total, item) => total + (item.status === 'PAID' ? item.amount : 0),
      0,
    );
    const totalRow = worksheet.addRow([
      `รวมทั้งหมด (${items.length} รายการ)`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      expenseTotal,
      '',
      '',
      '',
    ]);
    totalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.border = this.thinBorder();
    });
    totalRow.getCell(15).numFmt = '#,##0.00';

    return headerRow;
  }

  private styleHeaderRow(row: Row, columnCount: number): void {
    row.height = 30;
    for (let column = 1; column <= columnCount; column += 1) {
      const cell = row.getCell(column);
      cell.font = { bold: true, color: { argb: 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = this.thinBorder();
    }
  }

  private setDocumentLink(cell: Cell, documentUrl: string | null): void {
    if (!documentUrl) return;
    if (/^https?:\/\//i.test(documentUrl)) {
      cell.value = {
        text: 'เปิดสลิปโอนจ่าย',
        hyperlink: documentUrl,
        tooltip: documentUrl,
      };
      cell.font = { color: { argb: 'FF0369A1' }, underline: true };
      return;
    }
    cell.value = documentUrl;
  }

  private thinBorder() {
    const side = { style: 'thin' as const, color: { argb: 'FFE2E8F0' } };
    return { top: side, left: side, bottom: side, right: side };
  }

  private toExcelCalendarDate(value: string): Date {
    const date = new Date(value);
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  private formatThaiDate(value: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  private formatThaiTime(value: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Bangkok',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(value));
  }

  private categoryLabel(category: ExpenseLedgerItem['category']): string {
    return category === 'EXPENSE' ? 'ค่าใช้จ่าย' : 'คอมมิชชั่น';
  }

  private paymentMethodLabel(item: ExpenseLedgerItem): string {
    const methodLabel = {
      CASH: 'เงินสด',
      BANK_TRANSFER: 'โอนผ่านธนาคาร',
      CREDIT_CARD: 'บัตรเครดิต',
    }[item.paymentMethod];
    const bankAccount = [item.bankName, item.accountNumber].filter(Boolean).join(' · ');
    return bankAccount ? `${methodLabel}\n${bankAccount}` : methodLabel;
  }

  private statusLabel(status: ExpenseLedgerItem['status']): string {
    return status === 'PAID' ? 'ชำระแล้ว' : 'ยกเลิก';
  }
}
